import { BookingChangeKind } from '@/features/dashboard/bookingChanges/types/dto/BookingChangeKind.ts';
import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { BillRowVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';

/**
 * Model widoku paczki zmian — szkicu i paczek, które weszły już w życie.
 *
 * Wszystko tutaj przestaje istnieć, gdy rezerwacja nie ma otwartego szkicu;
 * rachunek, na który paczka się nakłada, stoi w `BillViewModel.ts` — razem
 * z regułą, że kwota modelu widoku jest listą po jednej pozycji na walutę.
 */

export enum ChangePackageStatus {
  DRAFT = 'DRAFT',
  AWAITING_CLIENT = 'AWAITING_CLIENT',
  APPLIED = 'APPLIED',
  /**
   * Klient odrzucił paczkę albo nie zdążył odpowiedzieć. Osobny stan od
   * `CANCELLED`: paczkę anulowaną zdjął operator, odrzuconą — klient, a operator
   * musi widzieć, która z tych rzeczy się wydarzyła.
   */
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

/**
 * Wpis karty „Ostatnie zmiany” i tabeli „Historia paczek”.
 *
 * Czego tu nie ma: zakresu paczki („Bobin Nowa · warsztaty”). Lista paczek go
 * nie niesie i nieść nie będzie — nazwy wariantów siedzą w katalogu, a nie
 * w pozycji paczki, więc zakres wymaga wejścia na szczegóły.
 */
export interface ChangePackageHistoryVm {
  id: string;
  packageNumber: number;
  status: ChangePackageStatus;
  /** Rezygnacja to inna operacja niż edycja pozycji i tak ma się czytać. */
  kind: BookingChangeKind;
  /** `null`, gdy paczkę założyło żądanie bez rozpoznanego pracownika. */
  author: string | null;
  /** Wprowadzenie paczki, a dla paczki, która nie weszła w życie — założenie. */
  occurredAt: string;
  itemCount: number;
  /**
   * `null` znaczy „kwoty nie ma”, nie „zero”: paczka odrzucona i anulowana
   * nigdy nie stanęła w rachunku, a otwartej kwotę podaje ekran szczegółów.
   * Kwota jest listą po jednej pozycji na walutę — paczka potrafi ruszyć
   * i złotówki, i euro naraz.
   */
  impact: MoneyValueDto[] | null;
  /** Co napisał zapisujący, odrzucając paczkę; `null`, gdy nic nie dopisał. */
  rejectionComment: string | null;
}

/** Wiersz wpływu paczki per osoba — karta „Wpływ paczki” i modal wyjścia. */
export interface DraftImpactEntryVm {
  personId: string;
  label: string;
  delta: MoneyValueDto[];
  /** Pozycja bez skutku finansowego — „bez wpływu”. */
  withoutImpact?: boolean;
}

/** Otwarty szkic paczki — spina panel boczny, belkę nagłówka i modal wyjścia. */
export interface ChangeDraftVm {
  id: string;
  bookingId: string;
  itemCount: number;
  /** `null`, gdy paczkę założyło żądanie bez rozpoznanego pracownika. */
  author: string | null;
  /** ISO-8601 instant; `null`, dopóki paczka nie była jeszcze zapisywana. */
  lastSavedAt: string | null;
  totalDelta: MoneyValueDto[];
  bookingValueAfter: MoneyValueDto[];
  impactByPerson: DraftImpactEntryVm[];
}

/**
 * Czego dotyczy przycisk dopisujący pozycję ręczną. Widok składa te parametry
 * z danych, które i tak renderuje, więc `addManualLine` wchodzi w prop bez
 * domykania po drodze — patrz `ParticipantBillView` i `BookingBillView`.
 */
export interface AddManualLineParams {
  /**
   * Osoba, do której rachunku trafi pozycja; `null` znaczy całą rezerwację
   * („+ Zniżka” na ekranie rezerwacji).
   */
  participantId?: string | null;
  /** Sekcja, z której przyszedł przycisk; brak zostawia wybór rachunkowi. */
  sectionId?: string | null;
  /**
   * Pozycja odejmuje od rachunku — formularz podpowiada wtedy kwotę ujemną.
   * To sama podpowiedź: znak wolno zmienić w obie strony.
   */
  discount?: boolean;
}
